import React from 'react';
import {CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PostAddIcon from '@mui/icons-material/PostAdd';
import UndoIcon from '@mui/icons-material/Undo';

/**
 * TableRowActionsMenu - Context menu for table row actions.
 *
 * Items adapt to the report context (drafts / approved FINTEL / disclosures), mirroring the
 * original per-category menus. Actions are only rendered when their handler is provided.
 *
 * @param {object} anchorEl - Anchor element for the menu
 * @param {boolean} open - Whether the menu is open
 * @param {function} onClose - Callback to close the menu
 * @param {string} role - User role (owner/viewer)
 * @param {function} onExport - Callback for export action
 * @param {boolean} exporting - Whether export is in progress
 * @param {function} onShareOpen - Callback to open share dialog
 * @param {function} onDeleteOpen - Callback to open delete dialog
 * @param {boolean} canDelete - Whether the delete action should be shown
 * @param {function} onCreateDisclosure - Callback for create disclosure action (approved FINTEL only)
 * @param {function} onResetToDraft - Callback for reset-to-draft action (approved FINTEL only)
 * @param {boolean} resettingToDraft - Whether reset-to-draft is in progress
 */
export default function TableRowActionsMenu({
                                                anchorEl,
                                                open,
                                                onClose,
                                                role,
                                                onExport,
                                                exporting = false,
                                                onShareOpen,
                                                onDeleteOpen,
                                                canDelete = false,
                                                onCreateDisclosure,
                                                onResetToDraft,
                                                resettingToDraft = false
                                            }) {
    const handleCloseAnd = (fn) => () => {
        if (typeof onClose === 'function') onClose();
        if (typeof fn === 'function') fn();
    };

    return (
        <Menu
            id="table-row-actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
        >
            {onCreateDisclosure && (
                <MenuItem onClick={handleCloseAnd(onCreateDisclosure)}>
                    <ListItemIcon>
                        <PostAddIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Create disclosure draft</ListItemText>
                </MenuItem>
            )}

            <MenuItem onClick={handleCloseAnd(onExport)} disabled={exporting}>
                <ListItemIcon>
                    {exporting
                        ? <CircularProgress size={16} />
                        : <FileDownloadOutlinedIcon fontSize="small"/>
                    }
                </ListItemIcon>
                <ListItemText>{exporting ? 'Exporting…' : 'Export'}</ListItemText>
            </MenuItem>

            {role === 'owner' && (
                <MenuItem onClick={handleCloseAnd(onShareOpen)}>
                    <ListItemIcon>
                        <ShareOutlinedIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Share</ListItemText>
                </MenuItem>
            )}

            {onResetToDraft && (
                <MenuItem onClick={handleCloseAnd(onResetToDraft)} disabled={resettingToDraft}>
                    <ListItemIcon>
                        {resettingToDraft
                            ? <CircularProgress size={16} />
                            : <UndoIcon fontSize="small"/>
                        }
                    </ListItemIcon>
                    <ListItemText>{resettingToDraft ? 'Resetting…' : 'Reset to Draft'}</ListItemText>
                </MenuItem>
            )}

            {canDelete && (
                <MenuItem onClick={handleCloseAnd(onDeleteOpen)} sx={{color: 'error.main'}}>
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" sx={{color: 'error.main'}}/>
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            )}
        </Menu>
    );
}
